import { Card, CardBody } from '@change/ds-core-ui/Card';
import { Link } from '@change/ds-core-ui/Link';
import { Text } from '@change/ds-core-ui/Text';
import { GovBuildingFilledIcon } from '@change/ds-icons/GovBuildingFilledIcon';
import { TrashIcon } from '@change/ds-icons/TrashIcon';

import { TranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import type { DecisionMaker } from '../../types';
import { stripTrailingCitation } from '../../utils';

export const DecisionMakerCard: React.FC<Props> = ({ decisionMaker, onRemove }) => {
	const { translate } = useI18n();

	return (
		<Card className="bg-background-base w-full [--card-padding:1.5rem]" data-qa="decision-maker-card">
			<CardBody className="flex flex-col">
				<div className="flex items-center gap-4">
					{/* a placeholder until decision makers have photos */}
					<span className="bg-fill-information-weaker text-icon-information flex size-10 shrink-0 items-center justify-center rounded-full">
						<GovBuildingFilledIcon size="medium" />
					</span>
					{/* stacked on mobile, one line from md up; the locale decides the order and wraps its separator in a
					`separator` span, which only shows on one line */}
					<Text as="p" className="[&_.separator]:hidden md:[&_.separator]:inline">
						{/* JSX replacements so the values land as escaped React children, not parsed as markup */}
						<TranslateHtml
							value="corgi.start_a_petition.decision_makers.name_position"
							replacements={{
								name: (
									<Text fontWeight="bold" className="block md:inline">
										{decisionMaker.name}
									</Text>
								),
								position: <span className="block md:inline">{decisionMaker.position}</span>,
							}}
						/>
					</Text>
				</div>
				<Text as="p" className="text-typography-weak mt-2 max-w-[65ch]">
					{stripTrailingCitation(decisionMaker.reasoning)}
				</Text>
				{onRemove && (
					<div className="mt-4 flex">
						<Link
							as="button"
							type="button"
							variant="secondary"
							leadingIcon={<TrashIcon />}
							// starts with the visible text so voice control can match it, then says who
							aria-label={translate('corgi.start_a_petition.decision_makers.remove_aria', { name: decisionMaker.name })}
							onClick={onRemove}
						>
							{translate('corgi.start_a_petition.decision_makers.remove')}
						</Link>
					</div>
				)}
			</CardBody>
		</Card>
	);
};

type Props = {
	decisionMaker: DecisionMaker;
	/** Omit for a read-only card (no Remove button). */
	onRemove?: () => void;
};
